import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, CurrencyFormatter, inject, ModuleIsEnabledFilter, Installer} from '@gongxigames/framework/core';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {FreeSpinsBasicState} from '@gongxigames/framework/slot/modules/freespins/states/FreeSpinsBasicState';
import {FreeSpinsInProgressState} from '@gongxigames/framework/slot/modules/freespins/states/FreeSpinsInProgressState';
import {FreeSpinsRetriggerState} from '@gongxigames/framework/slot/modules/freespins/states/FreeSpinsRetriggerState';
import {FreeSpinsIntroState} from '@gongxigames/framework/slot/modules/freespins/states/FreeSpinsIntroState';

export class FreeSpinsModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('FreeSpinsModule');
    public static readonly isEnabled: ModuleIsEnabledFilter = (components: Installer) => {
        return components.getComponent<GameConfig>(CoreDI.GameConfig).freeSpins || false;
    };

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly IN_PROGRESS_STATE: string = 'inProgress';
    public static readonly INTRO_STATE: string = 'intro';
    public static readonly RETRIGGER_STATE: string = 'retrigger';

    public static readonly FREESPIN_START_SPIN: symbol = Symbol('FREESPIN_START_SPIN');
    public static readonly FREESPINS_FINISHED: symbol = Symbol('FREESPINS_FINISHED');

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    protected setupEvents(): void {
        this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_WIN);
        this.onCurrentStateHandle(FlowModuleEvents.FREE_SPINS_FINISHED);
        this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_RESTORED);
        this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(ReelsEvents.ALL_REELS_STOPPED);
        this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
        this.onCurrentStateHandle(FlowModuleEvents.FREESPINS_RETRIGGER_WIN);
        this.onCurrentStateHandle(GenericPopupEvents.INTRO_STARTED);
        this.onCurrentStateHandle(GenericPopupEvents.INTRO_ANIMATION_STARTED);
        this.onCurrentStateHandle(GenericPopupEvents.FADE_OUT_STARTED);
        this.onCurrentStateHandle(GenericPopupEvents.POPUP_CLOSED);
    }

    public getValueByType(type: Symbol): string {
        switch (type) {
            case GenericPopupType.FREESPIN_INTRO:
                return this.getFreeGamesCount();

            case GenericPopupType.FREESPIN_OUTRO:
                return this.getFreeGamesWin();

            case GenericPopupType.FREESPIN_RETRIGGER:
                return this.getRetriggerCount();

            default:
                break;
        }

        return '';
    }

    protected setupViews(): void {
    }

    protected setupStates(): void {
        this.addState(new FreeSpinsBasicState(this, FreeSpinsModule.BASIC_STATE));
        this.addState(new FreeSpinsInProgressState(this, FreeSpinsModule.IN_PROGRESS_STATE));
        this.addState(new FreeSpinsRetriggerState(this, FreeSpinsModule.RETRIGGER_STATE));
        this.addState(new FreeSpinsIntroState(this, FreeSpinsModule.INTRO_STATE));

        this.gotoState(FreeSpinsModule.BASIC_STATE);
    }

    protected getFreeGamesCount(): string {
        return this.logic.getFreeGamesCount().toString();
    }

    protected getFreeGamesWin(): string {
        const {logic, currencyFormatter} = this;

        return currencyFormatter.formatCents(logic.getTotalWin());
    }

    protected getRetriggerCount(): string {
        return `+${this.logic.getRetriggerCount()}`;
    }

    public get currencyFormatter(): CurrencyFormatter {
        return this._currencyFormatter;
    }
}
